import {
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import TextFieldWithCheck from '@/components/form/TextFieldWithCheck.tsx';
import SimpleRichTextEditor from '@/components/form/SimpleRichTextEditor.tsx';

import AddressFields from '@/components/ui/AddressFields.tsx';
import ComboField from '@/components/form/ComboField.tsx';
import React, { useCallback, useEffect } from 'react';
import { OpportunityFormData } from '@/features/opportunities/form/types.ts';
import { investmentTypeLabels } from '@/features/investments/constants.ts';
import { InvestmentType } from '@inversor-hub/types';
import CadastreFormField from '@/components/ui/CadastreFormField.tsx';
import { CadastrePropertySummary, CadastreRawData } from '@inversor-hub/types';

interface Props {
  formData: OpportunityFormData;
  setFormData: React.Dispatch<React.SetStateAction<OpportunityFormData>>;
  handleChangeField: (field: string, value: string | number) => void;
  handleSubmit: (e: React.FormEvent) => void;
  submitted: boolean;
}

const sellersOptions = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'más de 10'];

// TODO: handle all investment types
const investmentTypeOptions = Object.entries(investmentTypeLabels).filter(([key]) =>
  ['TRADITIONAL_RENT', 'ROOM_RENT', 'ROOM_SALE', 'FLIPPING'].includes(key)
);

const OpportunityMainDetailsSection = ({
  formData,
  setFormData,
  handleSubmit,
  handleChangeField,
  submitted,
}: Props) => {
  const checkFormCompleteness = useCallback(() => {
    return [
      'title',
      'description',
      'type',
      'locationDescription',
      'address',
      'postalCode',
      'city',
      'province',
    ].every((fieldName) => {
      const value = formData[fieldName as keyof typeof formData];
      return value !== '' && value !== null && value !== undefined;
    });
  }, [formData]);

  // Set sellers to 1 if type is ROOM_SALE
  useEffect(() => {
    if (formData.type === 'ROOM_SALE') {
      setFormData((prev) => ({
        ...prev,
        sellers: '1',
      }));
    }
  }, [formData.type, setFormData]);

  const handleChangeEvent = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChangeField(event.target.name, event.target.value);
  };

  const handleAddressChange = ({
    address,
    city,
    postalCode,
    province,
  }: {
    address: string;
    city: string;
    postalCode: string;
    province: string;
  }) => {
    setFormData((prev) => {
      return {
        ...prev,
        address,
        city,
        postalCode,
        province,
      };
    });
  };

  const handleApproximateAddressToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const showApproximateAddress = event.target.checked;
    setFormData((prev) => ({
      ...prev,
      showApproximateAddress,
    }));
  };

  const handleCadastreDataFilled = (cadastralData: CadastrePropertySummary) => {
    const updates: Partial<OpportunityFormData> = {};
    const rawData: CadastreRawData = cadastralData.raw || {};

    let fullAddress;
    if (rawData.address) {
      fullAddress = String(rawData.address);
    } else {
      const addressParts = [];
      if (rawData.streetType && rawData.streetName) {
        addressParts.push(`${rawData.streetType} ${rawData.streetName}`);
      } else if (rawData.streetName) {
        addressParts.push(String(rawData.streetName));
      }
      if (rawData.number) addressParts.push(`Nº ${rawData.number}`);
      if (rawData.block) addressParts.push(`Bloque ${rawData.block}`);
      if (rawData.staircase) addressParts.push(`Esc ${rawData.staircase}`);
      if (rawData.floor) addressParts.push(`Planta ${rawData.floor}`);
      if (rawData.door) addressParts.push(`Puerta ${rawData.door}`);
      fullAddress = addressParts.join(', ');
    }

    if (fullAddress) updates.address = fullAddress;
    if (rawData.postalCode) updates.postalCode = String(rawData.postalCode);
    if (rawData.municipality) updates.city = String(rawData.municipality);
    if (rawData.province) updates.province = String(rawData.province);
    if (rawData.builtSurface) {
      const surface = Number(rawData.builtSurface);
      if (!isNaN(surface)) updates.squareMeters = surface;
    }
    if (rawData.constructionYear) {
      const year = Number(rawData.constructionYear);
      if (!isNaN(year)) updates.buildingYear = String(year);
    }
    if (rawData.floor) {
      updates.buildingFloor = String(rawData.floor);
    }

    setFormData((prev) => ({ ...prev, ...updates }));
  };

  return (
    <form name="generalOpportunityData" onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <TextFieldWithCheck
          id="title"
          name="title"
          label="Titulo (*)"
          variant="outlined"
          value={formData.title}
          onChange={handleChangeEvent}
        />
        <SimpleRichTextEditor
          value={formData.description}
          onChange={(value) => handleChangeField('description', value)}
          label="Descripcion (*)"
          placeholder="Describe la oportunidad de inversión con detalle. Usa los botones para dar formato al texto."
          required
        />

        <Stack spacing={1}>
          <TextField
            select
            label="Tipo de operación"
            value={formData.type}
            onChange={(e) => handleChangeField('type', e.target.value as InvestmentType)}
            fullWidth
          >
            {investmentTypeOptions.map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="caption" color="text.secondary">
            Selecciona el tipo de inversión que mejor describa esta oportunidad
          </Typography>
        </Stack>

        <CadastreFormField
          value={formData.cadastralReference}
          onChange={handleChangeEvent}
          onDataFilled={handleCadastreDataFilled}
        />

        <AddressFields formData={formData} onChange={handleAddressChange} />

        <FormControlLabel
          control={
            <Checkbox
              checked={formData.showApproximateAddress}
              onChange={handleApproximateAddressToggle}
            />
          }
          label="Ocultar dirección exacta a los inversores"
        />
        <TextFieldWithCheck
          id="locationDescription"
          name="locationDescription"
          label="Descripcion ubicación (*)"
          variant="outlined"
          value={formData.locationDescription}
          onChange={handleChangeEvent}
        />

        {formData.type !== 'ROOM_SALE' && (
          <Stack spacing={1}>
            <ComboField
              value={formData.sellers}
              onChange={handleChangeField}
              options={sellersOptions}
              label="¿Cuántos vendedores son?"
              name="sellers"
              id="sellers"
            />
            <Typography variant="caption" color="text.secondary">
              (cuantos más sean, más fácil será que nos rebajen una cantidad, ya que impacta menos)
            </Typography>
          </Stack>
        )}

        <TextFieldWithCheck
          id="observations"
          name="observations"
          label="Observaciones"
          variant="outlined"
          value={formData.observations}
          onChange={handleChangeEvent}
        />

        {!submitted && (
          <Button
            variant="contained"
            size="large"
            type="submit"
            disabled={!checkFormCompleteness()}
          >
            Dar de alta
          </Button>
        )}
      </Stack>
    </form>
  );
};

export default OpportunityMainDetailsSection;
